/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Plus, FileDown, Trash2, Loader2, X, Lock, Mail, ArrowRight,
  ReceiptText, FileText, FileSpreadsheet, Settings2, Wallet, TrendingUp, AlertTriangle,
} from "lucide-react";

const KINDS = [
  { id: "factura", label: "Facturi", single: "Factură fiscală", icon: FileText },
  { id: "proforma", label: "Proforme", single: "Factură proformă", icon: FileSpreadsheet },
  { id: "chitanta", label: "Chitanțe", single: "Chitanță", icon: ReceiptText },
];

const STATUS = {
  draft: { label: "Ciornă", cls: "bg-[#1E293B] text-[#94A3B8]" },
  issued: { label: "Emisă", cls: "bg-[#0C2A45] text-[#38BDF8]" },
  paid: { label: "Plătită", cls: "bg-[#0C3320] text-[#4ADE80]" },
  overdue: { label: "Scadentă", cls: "bg-[#3A1414] text-[#F87171]" },
  cancelled: { label: "Anulată", cls: "bg-[#1E293B] text-[#64748B] line-through" },
};

export default function Invoices() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();
  const hasAccess = user?.is_admin || ["pro", "business"].includes(user?.plan);
  const [tab, setTab] = useState("factura");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [emailInv, setEmailInv] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const money = (v) => `${Number(v || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON`;
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d || ""; } };

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/invoices?kind=${tab}`),
        api.get("/invoices/summary"),
      ]);
      setRows(r.data);
      setSummary(s.data);
    } catch { /* gated */ } finally { setLoading(false); }
  };
  useEffect(() => { if (hasAccess) load(); else setLoading(false); }, [tab]);

  const download = async (inv) => {
    setBusyId(inv.id + "pdf");
    try {
      const token = localStorage.getItem("offerai_token");
      const r = await api.get(`/invoices/${inv.id}/pdf?auth=${token}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(inv.full_number || inv.kind)}.pdf`.replace(/\s+/g, "_");
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Eroare la descărcarea PDF-ului"); } finally { setBusyId(null); }
  };

  const del = async (inv) => {
    if (!window.confirm(`Ștergi ${inv.full_number || "documentul"}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Șters"); setRows((d) => d.filter((x) => x.id !== inv.id)); load(); }
    catch { toast.error("Eroare"); }
  };

  if (!hasAccess) {
    return (
      <div className="p-5 lg:p-10 max-w-3xl mx-auto" data-testid="invoices-locked">
        <div className="overline text-[#00BFFF]">Facturare</div>
        <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">Facturare & Chitanțe</h1>
        <div className="card-flat p-8 mt-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#111C30] flex items-center justify-center mx-auto mb-4">
            <Lock size={26} className="text-[#00BFFF]" />
          </div>
          <h2 className="font-display font-bold text-xl">Disponibil pe Pro și Business</h2>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-md mx-auto">Emite facturi fiscale, proforme și chitanțe cu numerotare automată, evidența plăților și trimitere pe email.</p>
          <button onClick={() => nav("/app/abonament")} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="invoicing-upgrade-btn">
            Vezi planurile <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  const summaryCards = [
    { label: "Total emis", value: summary?.total_issued, icon: FileText, color: "#38BDF8" },
    { label: "Încasat", value: summary?.paid, icon: Wallet, color: "#4ADE80" },
    { label: "De încasat", value: summary?.outstanding, icon: TrendingUp, color: "#FBBF24" },
    { label: "Restanțe", value: summary?.overdue, icon: AlertTriangle, color: "#F87171" },
  ];

  return (
    <div className="p-5 lg:p-10 max-w-6xl mx-auto" data-testid="invoices-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#00BFFF]">Facturare</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">Facturare & Chitanțe</h1>
          <p className="text-sm text-[#94A3B8] mt-2">Facturi fiscale, proforme și chitanțe — numerotare automată și evidența plăților.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSettingsOpen(true)} className="btn-ink p-2.5" title="Setări serii" data-testid="invoice-settings-btn"><Settings2 size={18} /></button>
          <button onClick={() => setPickerOpen(true)} className="btn-primary flex items-center gap-2" data-testid="new-invoice-btn"><Plus size={16} /> Document nou</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="invoices-summary">
        {summaryCards.map((c) => (
          <div key={c.label} className="card-flat p-4">
            <div className="flex items-center gap-2 text-xs text-[#94A3B8]"><c.icon size={14} style={{ color: c.color }} /> {c.label}</div>
            <div className="font-display font-extrabold text-lg mt-1.5" style={{ color: c.color }}>{money(c.value)}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1.5 mt-8 border-b border-[#1E293B]">
        {KINDS.map((k) => (
          <button key={k.id} onClick={() => setTab(k.id)} data-testid={`tab-${k.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k.id ? "border-[#00BFFF] text-white" : "border-transparent text-[#94A3B8] hover:text-[#E2E8F0]"}`}>
            <k.icon size={15} /> {k.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-[#00BFFF]" /></div>
      ) : rows.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-[#94A3B8] mt-6" data-testid="invoices-empty">
          Niciun document în această secțiune. Apasă „Document nou" pentru a crea.
        </div>
      ) : (
        <div className="mt-6 grid gap-3">
          {rows.map((inv) => {
            const st = STATUS[inv.status_display || inv.status] || STATUS.draft;
            return (
              <div key={inv.id} className="card-flat p-4 flex items-center gap-4" data-testid={`invoice-row-${inv.id}`}>
                <button onClick={() => nav(`/app/facturare/${inv.id}`)} className="flex-1 min-w-0 text-left" data-testid={`invoice-open-${inv.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{inv.full_number || "Ciornă"}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="text-xs text-[#94A3B8] mt-0.5 truncate">{inv.client?.name || "—"} · {fmtDate(inv.issue_date)}</div>
                </button>
                <div className="text-right shrink-0">
                  <div className="font-display font-bold text-sm">{money(inv.totals?.total)}</div>
                  {inv.kind !== "chitanta" && inv.balance > 0 && <div className="text-[11px] text-[#FBBF24]">rest {money(inv.balance)}</div>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => download(inv)} disabled={busyId === inv.id + "pdf"} className="btn-ink p-2" title="PDF" data-testid={`invoice-pdf-${inv.id}`}>
                    {busyId === inv.id + "pdf" ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                  </button>
                  <button onClick={() => setEmailInv(inv)} className="btn-ink p-2" title="Trimite email" data-testid={`invoice-email-${inv.id}`}><Mail size={16} /></button>
                  <button onClick={() => del(inv)} className="btn-ink p-2 text-[#EF4444]" title="Șterge" data-testid={`invoice-del-${inv.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setPickerOpen(false)} data-testid="invoice-picker">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0B111D] border border-[#1E293B] w-full max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#1E293B]">
              <div className="font-display font-bold text-lg">Alege tipul documentului</div>
              <button onClick={() => setPickerOpen(false)} className="text-[#94A3B8] hover:text-[#E2E8F0]"><X size={18} /></button>
            </div>
            <div className="p-4 grid sm:grid-cols-3 gap-3">
              {KINDS.map((k) => (
                <button key={k.id} onClick={() => nav(`/app/facturare/nou/${k.id}`)} className="card-flat p-4 text-left hover:border-[#00BFFF] transition-colors" data-testid={`pick-${k.id}`}>
                  <k.icon size={22} className="text-[#00BFFF]" />
                  <div className="font-semibold text-sm mt-2">{k.single}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {emailInv && <SendInvoiceEmail inv={emailInv} onClose={() => setEmailInv(null)} onSent={load} />}
      {settingsOpen && <InvoiceSettings onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

function SendInvoiceEmail({ inv, onClose, onSent }) {
  const [email, setEmail] = useState(inv.client?.email || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!email) return toast.error("Introdu adresa de email");
    setBusy(true);
    try { await api.post(`/invoices/${inv.id}/send-email`, { to_email: email, message: message || null }); toast.success("Email trimis"); onSent?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eroare la trimitere"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="invoice-email-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0B111D] border border-[#1E293B] w-full max-w-md rounded-2xl p-5">
        <div className="font-display font-bold text-lg mb-3">Trimite {inv.full_number || "documentul"}</div>
        <label className="text-xs text-[#94A3B8]">Email destinatar</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} className="input-flat w-full mt-1" placeholder="client@email.com" data-testid="invoice-email-input" />
        <label className="text-xs text-[#94A3B8] mt-3 block">Mesaj (opțional)</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="input-flat w-full mt-1" />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ink">Anulează</button>
          <button onClick={send} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="invoice-email-send">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Trimite
          </button>
        </div>
      </div>
    </div>
  );
}

function InvoiceSettings({ onClose }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/invoice-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/invoice-settings", s); toast.success("Setări salvate"); onClose(); }
    catch { toast.error("Eroare"); } finally { setBusy(false); }
  };
  if (!s) return null;
  const field = (k, label) => (
    <div>
      <label className="text-xs text-[#94A3B8]">{label}</label>
      <input value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="input-flat w-full mt-1" data-testid={`setting-${k}`} />
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="invoice-settings-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0B111D] border border-[#1E293B] w-full max-w-md rounded-2xl p-5">
        <div className="font-display font-bold text-lg mb-4">Setări facturare</div>
        <div className="grid grid-cols-3 gap-3">
          {field("factura_series", "Serie factură")}
          {field("proforma_series", "Serie proformă")}
          {field("chitanta_series", "Serie chitanță")}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs text-[#94A3B8]">TVA implicit (%)</label>
            <input type="number" value={s.default_vat_rate ?? 19} onChange={(e) => setS({ ...s, default_vat_rate: parseFloat(e.target.value) })} className="input-flat w-full mt-1" data-testid="setting-vat" />
          </div>
          <div>
            <label className="text-xs text-[#94A3B8]">Termen scadență (zile)</label>
            <input type="number" value={s.due_days ?? 15} onChange={(e) => setS({ ...s, due_days: parseInt(e.target.value) })} className="input-flat w-full mt-1" data-testid="setting-due" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs text-[#94A3B8]">Mențiuni implicite pe factură</label>
          <textarea value={s.invoice_notes ?? ""} onChange={(e) => setS({ ...s, invoice_notes: e.target.value })} rows={2} className="input-flat w-full mt-1" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ink">Anulează</button>
          <button onClick={save} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="save-invoice-settings">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} Salvează
          </button>
        </div>
      </div>
    </div>
  );
}
